/**
 * Elevations for the straight "bridge" drawn between a routed endpoint and an
 * attached item (POI/waypoint/highlight) the routing network can't reach.
 *
 * The bridge is real ground the user walks, so it gets real terrain elevations
 * sampled from the HiiKER elevation service (see hiikerElevation) — a flat
 * bridge would erase a genuine climb down to a valley hotel just as surely as
 * a zero would invent one.
 *
 * These helpers cover what happens when that sampling is unavailable. An item
 * carries no altitude of its own — a POI is a name and a position — so its
 * marker's `ele` is routinely 0, and hiikerElevation itself falls back to a
 * plain straight line if the service is unreachable. Writing a bare 0 into the
 * line puts a sea-level point beside a routed one at 300m, and the profile
 * reads the drop as several hundred metres of descent that was never walked.
 * Carrying the last known elevation forward instead degrades to flat: wrong in
 * the small, but never fictional in the large.
 */

/**
 * A literal 0 is treated as "unknown" rather than "sea level". A POI genuinely
 * at sea level sits beside a route also at sea level, so carrying the previous
 * value forward lands in the same place — whereas trusting a spurious 0 beside
 * a mountain route invents the whole descent.
 */
function usableElevation(value: number | null | undefined): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value !== 0;
}

/**
 * Elevation to give a point with no usable one of its own: the last known
 * elevation, falling back to 0 only when nothing is known at all.
 */
export function bridgeElevation(
  itemEle: number | null | undefined,
  neighbourEle: number | null | undefined
): number {
  if (usableElevation(itemEle)) return itemEle as number;
  if (usableElevation(neighbourEle)) return neighbourEle as number;
  return 0;
}

/**
 * `[lon, lat, ele]` for a point sitting on an attached item, with the
 * elevation resolved against the routed coordinate it joins onto. Used when
 * there is no sampled bridge to draw from at all.
 */
export function bridgeCoordinate(
  lon: number,
  lat: number,
  itemEle: number | null | undefined,
  neighbour: number[] | undefined
): number[] {
  return [lon, lat, bridgeElevation(itemEle, neighbour?.[2])];
}

/**
 * Fill in any missing elevations across a sampled bridge.
 *
 * `coords` must run from the point ADJACENT to the routed line outward to the
 * item, so `anchorEle` (the routed endpoint's own elevation) seeds the
 * carry-forward. Points that came back with real elevations are left exactly
 * as sampled — this only patches gaps, so a working elevation service is
 * always what actually shapes the profile.
 */
export function fillBridgeElevations(
  coords: number[][],
  anchorEle: number | null | undefined
): number[][] {
  let lastKnown = usableElevation(anchorEle) ? (anchorEle as number) : 0;

  return coords.map((coord) => {
    if (usableElevation(coord[2])) {
      lastKnown = coord[2];
      return [coord[0], coord[1], coord[2]];
    }
    return [coord[0], coord[1], lastKnown];
  });
}
